/**
 * MatterOverviewCard — BM-H3723
 *
 * Vanessa, 5 Oct 2026: the matter's own page should carry the money bar,
 * the SharePoint folder, the parties with their contact details, and the
 * invoices with what has been paid. Backed by GET /api/matters/{ref}/overview
 * and the existing /money-on-account/{ref}/financial-summary.
 *
 * A failed read says so rather than showing an empty list: "no invoices" and
 * "could not load invoices" are different answers.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import MoneyBar from "./MoneyBar";
import StageQuoteBar from "./StageQuoteBar";
import { errorMessage } from "../utils/errorMessage";
import { FolderOpen, Users, Receipt, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

function fmtMoney(n) {
  if (n == null || isNaN(n)) return "—";
  return "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// Vanessa, 27 Oct 2026: "move the time card up to the top third of the page
// and merge with billing/invoice/WIP". timeSlot is the matter's Time block,
// shown under the money bar in this same card.
export default function MatterOverviewCard({ matterRef, onLoaded, refreshKey = 0, timeSlot = null }) {
  const enc = encodeURIComponent(matterRef);
  const [overview, setOverview] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [err, setErr] = useState("");
  const [moneyErr, setMoneyErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    setMoneyErr("");
    const [ov, fin] = await Promise.allSettled([
      axios.get(`${API}/matters/${enc}/overview`),
      axios.get(`${API}/money-on-account/${enc}/financial-summary`),
    ]);
    if (ov.status === "fulfilled") {
      setOverview(ov.value.data);
      if (onLoaded) onLoaded(ov.value.data);
    }
    else setErr(errorMessage(ov.reason, "Could not load parties and invoices"));
    if (fin.status === "fulfilled") setFinancials(fin.value.data);
    else setMoneyErr(errorMessage(fin.reason, "Could not load the money position"));
    setLoading(false);
  }, [enc]);

  // BM-H4483: refreshKey re-reads the overview, so a matter that has just
  // passed the chain shows its Time card without a page reload.
  useEffect(() => { load(); }, [load, refreshKey]);

  const parties = overview?.parties || [];
  const invoices = overview?.invoices || [];
  const folder = overview?.sharepoint_folder_url || "";

  return (
    <section className="mb-6 bg-white/[0.02] border border-white/10 rounded-lg p-5" data-testid="matter-overview-card">
      {/* Money bar + SharePoint folder */}
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <div className="flex-1 min-w-[260px]" data-testid="matter-money-bar">
          {financials ? (
            <MoneyBar
              quote={financials.quote || 0}
              wip={financials.wip || 0}
              moneyOnAccount={financials.money_on_account || 0}
              matterRef={matterRef}
              onEditQuote={load}
              showWhenEmpty
            />
          ) : null}
          {financials ? (
            <StageQuoteBar matterRef={matterRef} stage={financials.stage_quote || null} onSaved={load} />
          ) : moneyErr ? (
            <p className="text-xs text-status-red">{moneyErr}</p>
          ) : (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          )}
        </div>
        {folder ? (
          <a href={folder} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-blue-500/40 text-blue-300 hover:bg-blue-900/30"
             data-testid="matter-sharepoint-folder-link">
            <FolderOpen className="w-3.5 h-3.5" /> Open SharePoint folder
          </a>
        ) : overview ? (
          <span className="text-xs text-txt-muted" data-testid="matter-sharepoint-folder-missing">
            No SharePoint folder recorded for this matter
          </span>
        ) : null}
      </div>

      {timeSlot && <div className="mb-5 border-t border-white/10 pt-4">{timeSlot}</div>}

      {err && (
        <p className="text-xs text-status-red mb-3" data-testid="matter-overview-error">{err}</p>
      )}
      {loading && !overview && !err && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}

      {overview && (
        <div className={`grid gap-6 lg:grid-cols-2 ${timeSlot ? "border-t border-white/10 pt-4" : ""}`}>
          {/* Parties */}
          <div data-testid="matter-parties">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-teal" /> Parties
            </h2>
            {parties.length === 0 ? (
              <p className="text-xs text-txt-muted">No parties recorded yet.</p>
            ) : (
              <ul className="space-y-2">
                {parties.map((p, i) => (
                  <li key={`${p.name}-${i}`} className="text-xs border-b border-white/5 pb-2" data-testid="matter-party">
                    <div className="flex items-center gap-2">
                      {p.contact_id ? (
                        <Link to={`/contacts/${encodeURIComponent(p.contact_id)}`} className="font-semibold text-white hover:underline">
                          {p.name}
                        </Link>
                      ) : (
                        <span className="font-semibold text-white">{p.name}</span>
                      )}
                      <span className="text-txt-muted">{p.role}</span>
                    </div>
                    <div className="text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
                      {p.email && <a href={`mailto:${p.email}`} className="hover:underline">{p.email}</a>}
                      {p.phone && <a href={`tel:${p.phone}`} className="hover:underline">{p.phone}</a>}
                      {p.address && <span>{p.address}</span>}
                      {!p.email && !p.phone && !p.address && <span>No contact details recorded</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Invoices */}
          <div data-testid="matter-invoices">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2 mb-2">
              <Receipt className="w-4 h-4 text-teal" /> Invoices
              {overview.outstanding_total > 0 && (
                <span className="text-xs font-normal text-status-amber">
                  {fmtMoney(overview.outstanding_total)} outstanding
                </span>
              )}
            </h2>
            {invoices.length === 0 ? (
              <p className="text-xs text-txt-muted">No invoices on this matter yet.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-txt-muted border-b border-white/10">
                    <th className="py-1 pr-2 font-medium">Invoice</th>
                    <th className="py-1 pr-2 font-medium">Date</th>
                    <th className="py-1 pr-2 font-medium text-right">Total</th>
                    <th className="py-1 font-medium text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => (
                    <tr key={`${inv.invoice_number}-${i}`} className="border-b border-white/5" data-testid="matter-invoice-row">
                      <td className="py-1.5 pr-2 font-mono text-white">{inv.invoice_number || "Draft"}</td>
                      <td className="py-1.5 pr-2 text-slate-400">{fmtDate(inv.date)}</td>
                      <td className="py-1.5 pr-2 text-right text-slate-300">{fmtMoney(inv.total)}</td>
                      <td className="py-1.5 text-right">
                        {inv.paid ? (
                          <span className="inline-flex items-center gap-1 text-status-green">
                            <CheckCircle2 className="w-3 h-3" /> Paid{inv.paid_at ? ` ${fmtDate(inv.paid_at)}` : ""}
                          </span>
                        ) : inv.outstanding > 0 ? (
                          <span className="inline-flex items-center gap-1 text-status-amber">
                            <AlertCircle className="w-3 h-3" /> {fmtMoney(inv.outstanding)} due
                          </span>
                        ) : (
                          <span className="text-txt-muted">{inv.status}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
